import { useEffect, useState } from 'react';
import { Col, Form, message } from 'antd';
import { BookyButton } from '../Buttons/BookyButton';
import { UserAddOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { MemberService } from '../../Services/MemberService';
import { IntegrationService } from '../../Services/IntegrationService';
import { WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiMemberDtoMemberDto, WebApiMemberDtoCustomMemberFieldValueDto, WebApiModelsMemberField, WebApiModelsMembershipProduct } from '../../Repository/eventbookyapi';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { OneFrontModal } from '../Modals/OneFrontModal';
import { GroupType } from '../../Types/GroupType';
import { validateMemberDuplicates } from '../../Helpers/MemberValidationHelper';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { OutPutMemberEditFields } from '../Forms/OutPutMemberFields';
import { MembershipProductService } from '../../Services/MembershipProductService';

interface AddMemberProps {
    members: WebApiMemberDtoMemberDto[];
    groups: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[];
    membershipProducts: WebApiModelsMembershipProduct[];
    memberFieldSettings: WebApiModelsMemberField[];
    user: { hasOnlyReadRights: boolean; maxMembers: number };
    nextMemberIdSuggestion: string;
    loadMembers: () => void;
}

export const AddMember: React.FC<AddMemberProps> = (props) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false as boolean);
    const [warningConfirmed, setWarningConfirmed] = useState(false);
    const [fortnoxLinkConfirmed, setFortnoxLinkConfirmed] = useState(false);
    const [spirisLinkConfirmed, setSpirisLinkConfirmed] = useState(false);

    const GetText = TextService.GetText;

    const onCancel = () => {
        form.setFieldValue("groupName", "");
        setWarningConfirmed(false);
        setFortnoxLinkConfirmed(false);
        setSpirisLinkConfirmed(false);
        setOpen(false);
    };

    useEffect(() => {
        let groups: any = [];
        let defaultGroups: number[] = [];

        props.groups.forEach((group: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            if (!group.isDefaultGroup) {
                groups.push({ value: group.id, label: group.name });

                if (group.groupType == GroupType.Default) {
                    defaultGroups.push(group.id!);
                }
            }
        });
        setGroups(groups);
        form.resetFields();
        form.setFieldValue("uniqueId", props.nextMemberIdSuggestion);
        form.setFieldValue("groups", defaultGroups);
    }, [props])

    const setFieldCustomError = (fieldName: string, error: string,) => {
        form.setFields([
            {
                name: fieldName, // required
                errors: [error],
            },
        ]);
    }

    const onCreate = async (values: any) => {
        setLoading(true);

        // Validate member duplicates
        const validationResult = validateMemberDuplicates(
            values,
            props.members,
            props.memberFieldSettings
        );

        // Check max members limit
        if (props.members.length >= props.user.maxMembers) {
            setFieldCustomError("email", GetText("AddAdmin_MaxAllowedMembers"));
            setLoading(false);
            return true;
        }

        // Handle errors (blocking)
        if (validationResult.hasErrors) {
            validationResult.errors.forEach(error => {
                setFieldCustomError(error.fieldName, error.message);
            });
            setLoading(false);
            return true;
        }

        // Handle warnings (non-blocking with confirmation)
        if (validationResult.hasWarnings && !warningConfirmed) {
            const warningMessages = validationResult.warnings.map(w => w.message).join('\n\n');
            Modal.confirm({
                title: 'Varning',
                icon: <ExclamationCircleOutlined />,
                content: warningMessages,
                okText: 'Fortsätt ändå',
                cancelText: 'Avbryt',
                onOk: () => {
                    setWarningConfirmed(true);
                    form.submit();
                },
                onCancel: () => {
                    setLoading(false);
                }
            });
            setLoading(false);
            return true;
        }

        var memberProducts = [];

        // Combine membership and membershipAddon into memberProducts array
        if (values.membership) {
            memberProducts.push(values.membership);
        }

        if (values.membershipAddon && Array.isArray(values.membershipAddon)) {
            memberProducts.push(...values.membershipAddon);
        }

        const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

        props.memberFieldSettings.forEach((field: WebApiModelsMemberField) => {
            if (field.columnType === MemberFieldTypes.Custom) {
                customFields.push({
                    fieldId: field.id!,
                    fieldValue: values[field.id!]
                });
            }
        });

        // Check if the UniqueId already exists in Fortnox before saving
        if (values.uniqueId && !fortnoxLinkConfirmed) {
            const existingCustomerName = await IntegrationService.CheckFortnoxCustomer(values.uniqueId);
            if (existingCustomerName) {
                const content = GetText('Integrations_FortnoxCustomerExists_Content')
                    .replace('{0}', values.uniqueId)
                    .replace('{1}', existingCustomerName);
                Modal.confirm({
                    title: GetText('Integrations_FortnoxCustomerExists_Title'),
                    icon: <ExclamationCircleOutlined />,
                    content,
                    okText: GetText('Integrations_FortnoxCustomerExists_Link'),
                    cancelText: GetText('Integrations_FortnoxCustomerExists_Cancel'),
                    onOk: () => {
                        setFortnoxLinkConfirmed(true);
                        form.submit();
                    },
                    onCancel: () => {
                        setLoading(false);
                    }
                });
                setLoading(false);
                return;
            }
        }

        // Check if the UniqueId already exists in Spiris before saving
        if (values.uniqueId && !spirisLinkConfirmed) {
            const existingSpirisName = await IntegrationService.CheckSpirisCustomer(values.uniqueId);
            if (existingSpirisName) {
                const content = GetText('Integrations_CustomerExists_Content')
                    .replace('{integration}', 'Spiris')
                    .replace('{0}', values.uniqueId)
                    .replace('{1}', existingSpirisName);
                Modal.confirm({
                    title: GetText('Integrations_CustomerExists_Title').replace('{integration}', 'Spiris'),
                    icon: <ExclamationCircleOutlined />,
                    content,
                    okText: GetText('Integrations_FortnoxCustomerExists_Link'),
                    cancelText: GetText('Integrations_FortnoxCustomerExists_Cancel'),
                    onOk: () => {
                        setSpirisLinkConfirmed(true);
                        form.submit();
                    },
                    onCancel: () => {
                        setLoading(false);
                    }
                });
                setLoading(false);
                return;
            }
        }

        let newMemberId: number | null = null;
        try {
            newMemberId = await MemberService.AddMember({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                email2: values.email2,
                phoneNumber: values.phoneNumber,
                phoneNumber2: values.phoneNumber2,
                address: values.address,
                postalArea: values.postalArea,
                postalCode: values.postalCode,
                country: values.country,
                company: values.company,
                uniqueId: values.uniqueId,
                groups: values.groups,
                title: values.title,
                personalNumber: values.personalNumber,
                adressRow2: values.adressRow2,
                adressRow3: values.adressRow3,
                webPage: values.webPage,
                joinedDate: values.joinedDate,
                extra1: values.extra1,
                extra2: values.extra2,
                memberProducts: memberProducts,
                extra3: values.extra3,
                customFieldValues: customFields,
            });
        } catch (error) {
            message.warning(GetText("AddMember_ErrorSaving"));
            setLoading(false);
            return;
        }

        // If the admin confirmed linking to an existing Fortnox customer, write the snapshot now
        if (fortnoxLinkConfirmed && newMemberId) {
            try {
                await IntegrationService.LinkMemberToFortnox(newMemberId);
            } catch {
                // Non-blocking — member was created, link can be done manually from mismatch report
            }
            setFortnoxLinkConfirmed(false);
        }

        setLoading(false);
        setOpen(false);
        props.loadMembers();
    }

    const onPhoneChange = (value: any) => {
        form.setFieldValue("phoneNumber", value);
    }

    const onPhoneChange2 = (value: any) => {
        form.setFieldValue("phoneNumber2", value);
    }

    return (
        <>
            <OneFrontModal
                open={open}
                title={GetText("AddMember_Title")}
                okText={GetText("AddMember_Ok")}
                okButtonProps={{ 'data-testid': 'create-member-button' }}
                confirmLoading={loading}
                cancelText={GetText("AddMember_Cancel")}
                onCancel={onCancel}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
            >
                <Form colon={false} form={form} labelAlign={"left"} layout="vertical" onFinish={onCreate}>
                    <Col span={24}>
                        <OutPutMemberEditFields
                            memberFieldSettings={props.memberFieldSettings}
                            membershipProducts={props.membershipProducts}
                            GetText={GetText}
                            groups={groups}
                            user={props.user}
                            onPhoneChange={onPhoneChange}
                            onPhoneChange2={onPhoneChange2}
                            isAdmin={true}
                        />
                    </Col>
                </Form>
            </OneFrontModal>

            <BookyButton
                data-testid="add-member-button"
                type="primary"
                disabled={props.user?.hasOnlyReadRights}
                fullWidth={true}
                text={GetText("AddMember_AddButton")}
                onClick={() => {

                    setOpen(true);
                }}
                icon={<UserAddOutlined />}
            >
            </BookyButton>
        </>
    );
};

export default AddMember;